import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { Sidebar } from '~/components/sidebar/Sidebar';
import { ChatHeaderActions } from '~/components/header/Header';
import { openMobileSidebar } from '~/lib/stores/sidebar';
import { $isAuthenticated } from '~/lib/stores/auth';
import { $showAuthModal } from '~/lib/stores/authModal';
import { chatStore } from '~/lib/stores/chat';
import { workbenchStore } from '~/lib/stores/workbench';
import { description as descriptionAtom } from '~/lib/persistence/useChatHistory';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';

/**
 * Below `lg` the Sidebar becomes an off-canvas drawer, so the control that
 * opens it has to live in the layout. This bar is that control plus the two
 * things worth a permanent slot on a phone: the brand, and starting a build.
 * In a chat it is the only header — the chat name plus Preview/Code/Publish/
 * Share replace the desktop tab strip, so phones don't stack two bars.
 */
function MobileTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useStore($isAuthenticated);
  const description = useStore(descriptionAtom);
  const isOnChat = location.pathname.startsWith('/chat/');

  function handleNewChat() {
    chatStore.setKey('started', false);
    chatStore.setKey('aborted', false);
    chatStore.setKey('activeChat', '');
    descriptionAtom.set(undefined);
    workbenchStore.showWorkbench.set(false);
    navigate('/');
  }

  return (
    <header className="ui-root box-content flex h-13 flex-shrink-0 items-center gap-1 border-b border-hairline bg-surface pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))] pt-[env(safe-area-inset-top)] lg:hidden">
      <button
        type="button"
        onClick={openMobileSidebar}
        aria-label="Open projects"
        className="flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-secondary transition-colors hover:bg-raised hover:text-primary"
      >
        <span className="i-ph:list text-[19px]" />
      </button>

      <button
        type="button"
        onClick={handleNewChat}
        className="flex min-w-0 flex-shrink-0 cursor-pointer items-center gap-2 border-0 bg-transparent px-1 py-0"
        title="Dunea"
        aria-label={isOnChat ? 'Dunea home' : undefined}
      >
        <img src="/dunea-mark.svg" alt="" width={22} height={22} className="block flex-shrink-0" />
        {!isOnChat && <span className="ui-brand text-[16px] leading-none text-primary">Dunea</span>}
      </button>

      {isOnChat ? (
        <span className="min-w-0 flex-1 truncate pl-1 pr-1.5 text-[14px] font-medium text-primary">
          {description || 'Untitled'}
        </span>
      ) : (
        <div className="flex-1" />
      )}

      {isOnChat && <ChatHeaderActions compact />}

      {isOnChat ? null : isAuthenticated ? (
        <button
          type="button"
          onClick={handleNewChat}
          aria-label="New project"
          className="flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border border-accent/25 bg-raised text-accent transition-colors hover:border-accent/50"
        >
          <span className="i-ph:plus text-[16px]" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => $showAuthModal.set(true)}
          className="flex-shrink-0 cursor-pointer rounded-lg border border-subtle bg-transparent px-3.5 py-2 text-[12.5px] font-medium text-secondary transition-colors hover:border-prominent hover:text-primary"
        >
          Sign in
        </button>
      )}
    </header>
  );
}

/**
 * Shared shell for all authenticated pages.
 * Renders Sidebar once; child routes fill the <Outlet />.
 */
export default function AppLayout() {
  // Mounted (not just CSS-hidden) only below lg: it carries ChatHeaderActions, and a
  // second hidden copy next to the desktop Header would double its requests and state.
  const isCompact = useIsBelow(BREAKPOINTS.lg);

  return (
    <div className="flex h-full w-full flex-col bg-page transition-theme lg:flex-row">
      {isCompact && <MobileTopBar />}
      <Sidebar />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
